import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DatasetFieldSpec, DatasetFieldType } from "@/api/datasets";
import {
  Button,
  FormField,
  SelectField,
  TextInput,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { confirmDialogPanelVariants } from "@/components/ui/confirm-dialog";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { cn } from "@/lib/cn";
import {
  DATASET_FIELD_TYPES,
  FIELD_NAME_MAX_CHARS,
} from "./dataset-field-specs";

/** A select field's options, typed as one comma-separated line. */
function optionLabels(text: string): string[] {
  return text
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);
}

/**
 * Add field: a name and a type of the server's; a select field also takes
 * its options. The new field goes at the table's end, empty in every row.
 */
export function DatasetAddFieldDialog({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (spec: DatasetFieldSpec) => void;
}) {
  const t = useT(framesDatasetMessages);
  const tc = useT(commonMessages);
  const [name, setName] = useState("");
  const [type, setType] = useState<DatasetFieldType>("text");
  const [options, setOptions] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const labels = optionLabels(options);
  const ready =
    name.trim() !== "" && (type !== "select" || labels.length > 0) && !busy;

  useEffect(() => nameRef.current?.focus(), []);

  // A change on its way finishes first: Escape and the backdrop wait too.
  const cancel = () => {
    if (!busy) onCancel();
  };

  const submit = () => {
    if (!ready) return;
    onSubmit({
      name: name.trim(),
      type,
      ...(type === "select" ? { options: labels } : {}),
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[31800] flex items-center justify-center bg-black/30"
      onClick={cancel}
      data-interactive="true"
      data-testid="dataset-add-field-dialog-backdrop"
    >
      <form
        className={cn(
          surfaceRoleClassName("menu"),
          confirmDialogPanelVariants(),
        )}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          // An Escape inside the type menu (portalled) closes only the menu.
          if (
            event.key !== "Escape" ||
            !event.currentTarget.contains(event.target as Node)
          ) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          cancel();
        }}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dataset-add-field-title"
        data-testid="dataset-add-field-dialog"
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
      >
        <div className="border-b border-glass-stroke-faint px-5 py-3">
          <h2
            id="dataset-add-field-title"
            className="text-appkit-body font-semibold text-primary"
          >
            {t("addField")}
          </h2>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4">
          <FormField label={t("fieldName")} htmlFor="dataset-add-field-name">
            <TextInput
              ref={nameRef}
              id="dataset-add-field-name"
              value={name}
              maxLength={FIELD_NAME_MAX_CHARS}
              onChange={(event) => setName(event.target.value)}
              data-testid="dataset-add-field-name"
            />
          </FormField>
          <FormField label={t("fieldType")} htmlFor="dataset-add-field-type">
            <SelectField
              id="dataset-add-field-type"
              value={type}
              options={DATASET_FIELD_TYPES.map((value) => ({
                value,
                label: t(`type_${value}`),
              }))}
              onChange={(value) => setType(value as DatasetFieldType)}
              testId="dataset-add-field-type"
            />
          </FormField>
          {type === "select" && (
            <FormField
              label={t("fieldOptions")}
              htmlFor="dataset-add-field-options"
            >
              <TextInput
                id="dataset-add-field-options"
                value={options}
                placeholder={t("fieldOptionsHint")}
                onChange={(event) => setOptions(event.target.value)}
                data-testid="dataset-add-field-options"
              />
            </FormField>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-glass-stroke-faint px-5 py-3">
          <Button
            type="button"
            variant="secondary"
            onClick={cancel}
            disabled={busy}
            data-testid="dataset-add-field-cancel"
          >
            {tc("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!ready}
            data-testid="dataset-add-field-confirm"
          >
            {t("addField")}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
